// src/pages/LineagePage.jsx — The Lineage
// Twenty-six centuries of thinkers answering each other, as one scrollable
// conversation. Nodes light up as their module is completed; every node is
// always readable (information is never locked — the unlock is a celebration).
import React, { useEffect, useMemo, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Map, CheckCircle, ArrowRight, CornerDownRight, User } from 'lucide-react';
import { LINEAGE, TRADITIONS } from '../data/lineageMap';
import { getThinkerProfile } from '../data/thinkerProfiles';
import { TRADITION_STYLES } from '../components/lineage/traditionStyles';
import ThinkerProfileDrawer, { emphasise } from '../components/lineage/ThinkerProfileDrawer';
import { getAllModules } from '../data/modules';
import { useGamificationStore } from '../stores/gamificationStore';

const nameOf = (id) => LINEAGE.find(n => n.id === id)?.name || id;

const LineagePage = () => {
  const completedModules = useGamificationStore(s => s.completedModules) || [];
  const [filter, setFilter] = useState(null);
  const { thinkerId } = useParams();

  const modulesByOrder = useMemo(() => {
    const map = {};
    getAllModules().forEach(m => { map[m.order] = m; });
    return map;
  }, []);

  const metOrders = useMemo(() => {
    const orders = new Set();
    getAllModules().forEach(m => { if (completedModules.includes(m.id)) orders.add(m.order); });
    return orders;
  }, [completedModules]);

  const nodes = useMemo(
    () => [...LINEAGE].sort((a, b) => a.year - b.year).filter(n => !filter || n.tradition === filter),
    [filter]
  );

  const metCount = LINEAGE.filter(n => metOrders.has(n.module)).length;

  const scrollTo = (id) => document.getElementById(`lineage-${id}`)?.scrollIntoView({ behavior: 'smooth', block: 'center' });

  // A profile opened by URL keeps the timeline behind it at the right node,
  // so closing the drawer leaves the reader exactly where that thinker lives.
  const openNode = thinkerId ? LINEAGE.find(n => n.id === thinkerId) : null;
  useEffect(() => {
    if (openNode) {
      document.getElementById(`lineage-${openNode.id}`)?.scrollIntoView({ behavior: 'instant', block: 'center' });
    }
  }, [openNode]);

  return (
    <div className="min-h-screen bg-gradient-to-br from-charcoal via-gunmetal to-charcoal pt-28 pb-16">
      {/* Stars above the cloud deck: the voices across time */}
      <div className="relative h-44 sm:h-56 overflow-hidden mb-8" aria-hidden="true">
        <img
          src="/images/cloud-05.webp"
          alt=""
          className="w-full h-full object-cover object-[50%_20%]"
          onError={(e) => { e.target.style.display = 'none'; }}
        />
        <div className="absolute inset-0 bg-gradient-to-b from-charcoal/30 via-transparent to-charcoal" />
      </div>
      <div className="container mx-auto px-4 sm:px-6 max-w-3xl">
        <motion.div initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }}>
          <div className="flex items-center gap-3 mb-2">
            <Map className="w-8 h-8 text-conversation-teal" />
            <h1 className="text-3xl sm:text-4xl font-bold text-white">The Lineage</h1>
          </div>
          <p className="text-slate-300 mb-2 max-w-xl">
            Philosophy is one long conversation: thinkers answering thinkers across centuries
            and continents. Completing a module lights up the voices you have properly met.
          </p>
          {/* The curatorial note: why these thinkers, owned honestly */}
          <p className="text-sm text-slate-400 mb-2 max-w-xl leading-relaxed">
            Why these {LINEAGE.length}? Philosophy was born in at least three places (Greece,
            India and China), and this map refuses to pretend otherwise. It holds the thinkers
            the eight modules actually draw on, arranged so you can see who answered whom; most
            earned their place by changing what the next generation had to answer. Every voice
            on this map opens into a full profile: the life, the work, and why the
            conversation bent around them.
          </p>
          <p className="text-sm text-conversation-teal font-semibold mb-4">
            You have met {metCount} of {LINEAGE.length} voices.
          </p>

          <div className="flex flex-wrap gap-2 mb-8">
            <button
              onClick={() => setFilter(null)}
              aria-pressed={!filter}
              className={`px-3 py-1.5 rounded-full text-xs font-semibold border transition-colors ${
                !filter ? 'border-white text-white bg-white/10' : 'border-slate-600 text-slate-400 hover:text-white'
              }`}
            >
              All traditions
            </button>
            {Object.entries(TRADITIONS).map(([key, t]) => {
              const style = TRADITION_STYLES[key];
              return (
                <button
                  key={key}
                  onClick={() => setFilter(filter === key ? null : key)}
                  aria-pressed={filter === key}
                  className={`px-3 py-1.5 rounded-full text-xs font-semibold border transition-colors flex items-center gap-1.5 ${
                    filter === key ? `border-white text-white ${style.chip}` : `border-slate-600 ${style.text} hover:border-slate-400`
                  }`}
                >
                  <span className={`w-2 h-2 rounded-full ${style.dot}`}></span>
                  {t.label}
                </button>
              );
            })}
          </div>
        </motion.div>

        <div className="relative border-l-2 border-slate-700 ml-3 sm:ml-4 space-y-6">
          {nodes.map((node) => {
            const style = TRADITION_STYLES[node.tradition];
            const met = metOrders.has(node.module);
            const mod = modulesByOrder[node.module];
            const hasProfile = !!getThinkerProfile(node.id);
            return (
              <div key={node.id} id={`lineage-${node.id}`} className="relative pl-6 sm:pl-8">
                <span className={`absolute -left-[7px] top-6 w-3 h-3 rounded-full ${style.dot} ${met ? '' : 'opacity-40'}`}></span>
                {/* Unmet nodes dim via background, dot and the missing tick,
                    never via card-level opacity, which pushed the text below
                    readable contrast on the dark ground (Nigel, 2026-09-03) */}
                <div className={`rounded-xl border p-5 transition-all ${style.border} ${met ? 'bg-slate-800/60' : 'bg-slate-800/30'}`}>
                  <div className="flex items-start justify-between gap-3">
                    <div>
                      <div className="flex items-center gap-2 flex-wrap">
                        <h3 className="font-bold text-white">{node.name}</h3>
                        {met && <CheckCircle className="w-4 h-4 text-conversation-teal" />}
                        <span className={`text-xs px-2 py-0.5 rounded-full ${style.chip} ${style.text}`}>
                          {TRADITIONS[node.tradition].label}
                        </span>
                      </div>
                      <p className="text-xs text-slate-400 mt-0.5">{node.dates}</p>
                    </div>
                  </div>
                  <p className="text-sm text-slate-300 mt-2 leading-relaxed">{node.oneLiner}</p>
                  {!hasProfile && node.cutThrough && (
                    <p className={`text-xs text-slate-400 mt-2 leading-relaxed border-l-2 ${style.border} pl-2.5`}>
                      {emphasise(node.cutThrough)}
                    </p>
                  )}
                  {node.answers && node.answers.length > 0 && (
                    <p className="text-xs text-slate-400 mt-2 flex items-center gap-1 flex-wrap">
                      <CornerDownRight className="w-3 h-3" />
                      Answers:
                      {node.answers.map(id => (
                        <button
                          key={id}
                          onClick={() => scrollTo(id)}
                          className="inline-flex items-center min-h-[24px] underline decoration-dotted hover:text-white transition-colors"
                        >
                          {nameOf(id)}
                        </button>
                      ))}
                    </p>
                  )}
                  <div className="flex items-center gap-4 flex-wrap mt-3">
                    {hasProfile && (
                      <Link
                        to={`/lineage/${node.id}`}
                        className={`inline-flex items-center gap-1 text-xs font-semibold ${style.text} hover:text-white transition-colors`}
                      >
                        <User className="w-3 h-3" />
                        Full profile
                        <ArrowRight className="w-3 h-3" />
                      </Link>
                    )}
                    {mod && (
                      <Link
                        to={`/modules/${mod.id}`}
                        className={`inline-flex items-center gap-1 text-xs ${met ? 'text-slate-400 hover:text-white' : `${style.text} hover:text-white`} transition-colors`}
                      >
                        {met ? `Met in Module ${node.module}: ${mod.title}` : `Meet them in Module ${node.module}: ${mod.title}`}
                        <ArrowRight className="w-3 h-3" />
                      </Link>
                    )}
                  </div>
                </div>
              </div>
            );
          })}
        </div>

        <p className="text-xs text-slate-400 mt-10 max-w-xl">
          Lineage lines are a beginner's map, not a scholarly apparatus: most thinkers answer
          many more voices than an arrow can show, and several of these conversations ran through
          translation movements spanning centuries.
        </p>
      </div>

      {openNode && (
        <ThinkerProfileDrawer
          node={openNode}
          profile={getThinkerProfile(openNode.id)}
          met={metOrders.has(openNode.module)}
          module={modulesByOrder[openNode.module]}
        />
      )}
    </div>
  );
};

export default LineagePage;
